import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart3, CalendarDays, Calendar, Download, FileText, Layers, Trophy } from 'lucide-react';
import { adminReports, adminTradingGroups } from '@/api/admin';
import type { DailyReportRow, WeeklyReportPayload } from '@/api/extras';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Pill } from '@/components/widgets/Pill';
import { Sparkline } from '@/components/widgets/Sparkline';
import { fmtMoney, fmtPct, fmtPnLNumber, moneyToNumber } from '@/lib/format';
import { downloadCsv } from '@/lib/csv';
import { ApiError } from '@/lib/http';
import { cn } from '@/lib/utils';
import { toast } from '@/components/ui/sonner';

type Tab = 'daily' | 'weekly' | 'group' | 'custom';

export function Reports() {
  const [tab, setTab] = useState<Tab>('daily');
  const dailyQ = useQuery({ queryKey: ['admin', 'reports', 'daily'], queryFn: adminReports.daily });
  const data = dailyQ.data ?? [];

  const summary = useMemo(() => {
    if (!data.length) return null;
    return {
      totalPnl: data.every((r) => r.pnlRealized != null) ? data.reduce((s, r) => s + (r.pnlRealized ?? 0), 0) : null,
      avgWin: data.reduce((s, r) => s + r.winRatePct, 0) / data.length,
      totalSignals: data.reduce((s, r) => s + r.signalsTotal, 0),
      totalRiskEvents: data.reduce((s, r) => s + r.riskEvents, 0),
      equityLine: data.map((r) => r.mainEquity).filter((v): v is number => v != null),
      pnlLine: data.map((r) => r.pnlRealized).filter((v): v is number => v != null),
    };
  }, [data]);

  const exportCsv = () => {
    if (!data.length) return;
    const header = ['date', 'mainEquity', 'mainBalance', 'pnlRealized', 'signalsTotal', 'signalsExecuted', 'signalsBlocked', 'winRatePct', 'riskEvents'];
    const rows = data.map((r) => header.map((k) => String(r[k as keyof DailyReportRow])).join(','));
    const csv = [header.join(','), ...rows].join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `superaios-daily-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV 已导出');
  };

  return (
    <div className="p-6 space-y-4">
      <header>
        <h1 className="text-xl font-bold text-ink-0 flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-brand" />
          报表中心
        </h1>
        <p className="text-sm text-ink-2 mt-1">每日 / 每周 / 自定义 三类报表 · 数据源对照 §10.10 dashboard 聚合(§9.15)</p>
      </header>

      {/* 四类入口卡 */}
      <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        <EntryCard
          tone={tab === 'daily' ? 'active' : 'default'}
          onClick={() => setTab('daily')}
          icon={<CalendarDays className="h-5 w-5 text-brand" />}
          title="每日报表"
          desc="每日 04:00 自动生成 · 净值变化 · 跟单笔数 · 胜率 · 风控事件"
          status="已生成 30 天"
        />
        <EntryCard
          tone={tab === 'group' ? 'active' : 'default'}
          onClick={() => setTab('group')}
          icon={<Layers className="h-5 w-5 text-brand" />}
          title="分组日报"
          desc="交易时段组 × 交易日 · 人数 / 笔数 / 手数 / 已实现净额 / 胜负"
          status="收盘后落表"
        />
        <EntryCard
          tone={tab === 'weekly' ? 'active' : 'default'}
          onClick={() => setTab('weekly')}
          icon={<Calendar className="h-5 w-5 text-purple" />}
          title="每周报表"
          desc="每周一 04:00 · 雇员胜率排行 · 品种盈利 · 累计 PnL 曲线"
          status="(占位)"
        />
        <EntryCard
          tone={tab === 'custom' ? 'active' : 'default'}
          onClick={() => setTab('custom')}
          icon={<FileText className="h-5 w-5 text-warn" />}
          title="自定义报表"
          desc="选择时间范围 + 字段组合 → 导出 PDF / Excel"
          status="(占位)"
        />
      </section>

      {tab === 'daily' && (
        <>
          {summary && (
            <section className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <SumCard label="近 30 天已入账净额" value={fmtPnLNumber(summary.totalPnl)} tone={(summary.totalPnl ?? 0) >= 0 ? 'up' : 'down'} sub={
                summary.totalPnl == null ? '部分日期缺少估值，完整区间收益请看平台经营' : <Sparkline data={summary.pnlLine} width={180} height={28} tone={summary.totalPnl >= 0 ? 'up' : 'down'} />
              } />
              <SumCard label="平均胜率" value={fmtPct(summary.avgWin)} />
              <SumCard label="总信号数" value={String(summary.totalSignals)} sub="跨 30 天" />
              <SumCard label="风控事件" value={String(summary.totalRiskEvents)} tone={summary.totalRiskEvents > 0 ? 'warn' : 'neutral'} />
            </section>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>近 30 天每日报表</span>
                <Button variant="outline" size="sm" onClick={exportCsv} disabled={!data.length}>
                  <Download className="h-4 w-4" />
                  导出 CSV
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-bg-3 text-ink-2 text-xs">
                    <tr>
                      <th className="px-3 py-2 text-left font-normal">日期</th>
                      <th className="px-3 py-2 text-right font-normal">主账户净值</th>
                      <th className="px-3 py-2 text-right font-normal">已入账净盈亏</th>
                      <th className="px-3 py-2 text-right font-normal">信号</th>
                      <th className="px-3 py-2 text-right font-normal">成交</th>
                      <th className="px-3 py-2 text-right font-normal">拦截</th>
                      <th className="px-3 py-2 text-right font-normal">胜率</th>
                      <th className="px-3 py-2 text-right font-normal">风控事件</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.slice().reverse().map((r) => (
                      <tr key={r.date} className="border-t border-line row-hover">
                        <td className="px-3 py-2 num">{r.date}</td>
                        <td className="px-3 py-2 text-right num">{r.mainEquity == null ? '—' : `$${r.mainEquity.toFixed(2)}`}</td>
                        <td className={cn('px-3 py-2 text-right num font-medium', (r.pnlRealized ?? 0) > 0 ? 'text-up' : (r.pnlRealized ?? 0) < 0 ? 'text-down' : 'text-ink-3')}>
                          {fmtPnLNumber(r.pnlRealized)}
                        </td>
                        <td className="px-3 py-2 text-right num">{r.signalsTotal}</td>
                        <td className="px-3 py-2 text-right num text-up">{r.signalsExecuted}</td>
                        <td className="px-3 py-2 text-right num text-warn">{r.signalsBlocked}</td>
                        <td className={cn('px-3 py-2 text-right num', r.winRatePct >= 60 ? 'text-up' : r.winRatePct < 40 ? 'text-warn' : 'text-ink-1')}>
                          {r.signalsTotal > 0 ? fmtPct(r.winRatePct) : '—'}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {r.riskEvents > 0 ? <Pill tone="warn">{r.riskEvents}</Pill> : <span className="text-ink-3">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {tab === 'group' && <GroupDailyPanel />}

      {tab === 'weekly' && <WeeklyReport />}

      {tab === 'custom' && (
        <Card>
          <CardContent className="py-12 text-center text-sm text-ink-3 space-y-2">
            <FileText className="h-10 w-10 mx-auto opacity-40" />
            <div>自定义报表生成器待接入</div>
            <div className="text-xs">选择时间范围 + 字段组合 → 后端聚合 → 导出 PDF / Excel</div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

interface EntryCardProps {
  tone: 'active' | 'default';
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
  desc: string;
  status: string;
}

/** YYYY-MM-DD(本地日期)。 */
function isoDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * 分组日报(交易时段组 × 交易日)。数据源 = group_pnl_daily 落表,**当日数据要等当晚落表后才出现**。
 *
 * 口径提醒(踩过的坑):
 * - realizedNet 是 Money(digits=2),后端已按 `pnl + commission×100 − service_fee_cents` 换算落表,
 *   这里直接 fmtMoney,**不要**再 ÷100。
 * - lots100Total 是跨品种直接相加的**百分手**,只当活跃度量纲用,不可当敞口 / 名义价值。
 * - 交易日是**全局**日界(兜底翻篇,默认北京 03:00),分组不改日界;各组之和 = 全平台当日已实现净额。
 */
function GroupDailyPanel() {
  const today = new Date();
  const monthAgo = new Date(today.getTime() - 29 * 86400_000);
  const [from, setFrom] = useState(isoDate(monthAgo));
  const [to, setTo] = useState(isoDate(today));
  // '' = 全部组 + 未分组桶;'0' = 只看未分组
  const [groupId, setGroupId] = useState('');

  const groupsQ = useQuery({ queryKey: ['admin', 'trading-groups'], queryFn: adminTradingGroups.list });
  const q = useQuery({
    queryKey: ['admin', 'reports', 'group-daily', { from, to, groupId }],
    queryFn: () =>
      adminReports.groupDaily({
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
        ...(groupId ? { groupId } : {}),
      }),
  });
  const rows = q.data?.items ?? [];

  const summary = useMemo(() => {
    if (!rows.length) return null;
    const win = rows.reduce((s, r) => s + r.winCount, 0);
    const loss = rows.reduce((s, r) => s + r.lossCount, 0);
    return {
      net: rows.reduce((s, r) => s + moneyToNumber(r.realizedNet), 0),
      orders: rows.reduce((s, r) => s + r.orderCount, 0),
      lots: rows.reduce((s, r) => s + r.lots100Total, 0) / 100,
      winRate: win + loss > 0 ? (win / (win + loss)) * 100 : null,
      groups: new Set(rows.map((r) => r.tradingGroupId)).size,
    };
  }, [rows]);

  const exportCsv = () => {
    if (!rows.length) return;
    downloadCsv(
      'group-daily',
      rows,
      [
        { key: 'tradeDate', header: '交易日' },
        { key: 'tradingGroupId', header: '组ID' },
        { key: 'groupName', header: '时段组' },
        { key: 'userCount', header: '用户数' },
        { key: 'orderCount', header: '笔数' },
        { key: 'lots100Total', header: '手数', format: (_, r) => (r.lots100Total / 100).toFixed(2) },
        { key: 'realizedNet', header: '已实现净额(美元)', format: (_, r) => moneyToNumber(r.realizedNet).toFixed(2) },
        { key: 'winCount', header: '盈利单' },
        { key: 'lossCount', header: '亏损单' },
        {
          key: 'winRate',
          header: '胜率(%)',
          format: (_, r) => (r.winCount + r.lossCount > 0 ? Number(r.winRate ?? 0).toFixed(1) : ''),
        },
      ],
      {
        note: `分组日报 ${from || '最近30天'} ~ ${to || '今日'}${groupId ? ` · 组ID=${groupId}` : ' · 全部组'}｜交易日为全局日界(兜底翻篇);手数为跨品种相加的百分手÷100,仅作活跃度;金额为已实现净额(含服务费与佣金换算)`,
      },
    );
    toast.success('CSV 已导出');
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">筛选</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-ink-2" htmlFor="gd-from">起始交易日</Label>
              <Input id="gd-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-44 num" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-ink-2" htmlFor="gd-to">结束交易日</Label>
              <Input id="gd-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-44 num" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-ink-2" htmlFor="gd-group">时段组</Label>
              <select
                id="gd-group"
                className="h-9 rounded-md border border-line bg-bg-2 px-2 text-sm"
                value={groupId}
                onChange={(e) => setGroupId(e.target.value)}
              >
                <option value="">全部(含未分组)</option>
                <option value="0">未分组(继承全局)</option>
                {(groupsQ.data?.items ?? []).map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </div>
            <Button variant="outline" size="sm" onClick={exportCsv} disabled={!rows.length}>
              <Download className="h-4 w-4" />
              导出 CSV
            </Button>
          </div>
          <p className="mt-3 text-xs text-ink-3 leading-relaxed">
            交易日按<strong>全局</strong>日界(兜底翻篇,默认北京 03:00)划分,分组不改日界;
            同一交易日各组之和 = 全平台该日已实现净额。组归属取<strong>开仓时的快照</strong>,
            改渠道所属组不会让历史数字漂移。<strong>当日行是截至此刻的实时中间值</strong>,当晚收盘结算完仍会变动,
            对账请用已落表的历史日。
          </p>
        </CardContent>
      </Card>

      {summary && (
        <section className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <SumCard
            label="区间已实现净额"
            value={fmtPnLNumber(summary.net)}
            tone={summary.net >= 0 ? 'up' : 'down'}
            sub={`${summary.groups} 个分组桶`}
          />
          <SumCard label="结算笔数" value={String(summary.orders)} />
          <SumCard label="成交手数" value={summary.lots.toFixed(2)} sub="跨品种相加,仅作活跃度" />
          <SumCard label="区间胜率" value={summary.winRate == null ? '—' : fmtPct(summary.winRate)} />
        </section>
      )}

      <Card>
        <CardHeader>
          <CardTitle>分组日报 · {rows.length} 行</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {q.isError && (
            <div className="px-4 py-3 text-sm text-down-dim">
              无法加载分组日报:{q.error instanceof ApiError ? q.error.message : '未知错误'}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-bg-3 text-ink-2 text-xs">
                <tr>
                  <th className="px-3 py-2 text-left font-normal">交易日</th>
                  <th className="px-3 py-2 text-left font-normal">时段组</th>
                  <th className="px-3 py-2 text-right font-normal">用户数</th>
                  <th className="px-3 py-2 text-right font-normal">笔数</th>
                  <th
                    className="px-3 py-2 text-right font-normal"
                    title="跨品种直接相加的百分手 ÷100,只作活跃度量纲,不可当敞口 / 名义价值"
                  >
                    手数
                  </th>
                  <th className="px-3 py-2 text-right font-normal" title="已实现净额(含佣金与服务费换算),各组之和 = 全平台当日已实现净额">
                    已实现净额
                  </th>
                  <th className="px-3 py-2 text-right font-normal">盈利单</th>
                  <th className="px-3 py-2 text-right font-normal">亏损单</th>
                  <th className="px-3 py-2 text-right font-normal">胜率</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const net = moneyToNumber(r.realizedNet);
                  const decided = r.winCount + r.lossCount;
                  return (
                    <tr key={`${r.tradeDate}-${r.tradingGroupId}`} className="border-t border-line row-hover">
                      <td className="px-3 py-2 num">{r.tradeDate}</td>
                      <td className="px-3 py-2 text-ink-1">
                        {r.groupName}
                        {r.tradingGroupId === '0' && (
                          <span className="ml-1.5 text-xs text-ink-3">(未分组)</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right num">{r.userCount}</td>
                      <td className="px-3 py-2 text-right num">{r.orderCount}</td>
                      <td className="px-3 py-2 text-right num text-ink-2">{(r.lots100Total / 100).toFixed(2)}</td>
                      <td
                        className={cn(
                          'px-3 py-2 text-right num font-medium',
                          net > 0 ? 'text-up' : net < 0 ? 'text-down' : 'text-ink-3',
                        )}
                      >
                        {fmtMoney(r.realizedNet, { sign: true })}
                      </td>
                      <td className="px-3 py-2 text-right num text-up">{r.winCount}</td>
                      <td className="px-3 py-2 text-right num text-down">{r.lossCount}</td>
                      <td className="px-3 py-2 text-right num text-ink-1">
                        {decided > 0 ? fmtPct(r.winRate) : '—'}
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && !q.isLoading && (
                  <tr>
                    <td colSpan={9} className="px-3 py-8 text-center text-ink-3">
                      该区间暂无分组日报
                    </td>
                  </tr>
                )}
                {q.isLoading && (
                  <tr>
                    <td colSpan={9} className="px-3 py-8 text-center text-ink-3">
                      加载中...
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function WeeklyReport() {
  const weeklyQ = useQuery<WeeklyReportPayload>({ queryKey: ['admin', 'reports', 'weekly'], queryFn: adminReports.weekly });
  const data = weeklyQ.data;
  if (!data) return <Card><CardContent className="py-12 text-center text-sm text-ink-3">加载中...</CardContent></Card>;

  const cumulativeMax = Math.max(0, ...data.weeks.map((w) => Math.abs(w.cumulativePnl ?? 0)));

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>近 12 周累计已入账净额（覆盖以来）</span>
            <Pill tone={(data.weeks[data.weeks.length - 1]?.cumulativePnl ?? 0) >= 0 ? 'up' : 'down'}>
              当前累计 {fmtPnLNumber(data.weeks[data.weeks.length - 1]?.cumulativePnl)}
            </Pill>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-bg-3 text-ink-2 text-xs">
                <tr>
                  <th className="px-3 py-2 text-left font-normal">周次</th>
                  <th className="px-3 py-2 text-right font-normal">本周 PnL</th>
                  <th className="px-3 py-2 text-right font-normal">累计 PnL</th>
                  <th className="px-3 py-2 text-right font-normal">信号</th>
                  <th className="px-3 py-2 text-right font-normal">胜率</th>
                  <th className="px-3 py-2 font-normal w-1/3">累计趋势</th>
                </tr>
              </thead>
              <tbody>
                {data.weeks.map((w) => (
                  <tr key={w.weekLabel} className="border-t border-line row-hover">
                    <td className="px-3 py-2 num text-xs">{w.weekLabel}</td>
                    <td className={cn('px-3 py-2 text-right num font-medium', (w.weeklyPnl ?? 0) >= 0 ? 'text-up' : 'text-down')}>
                      {fmtPnLNumber(w.weeklyPnl)}
                    </td>
                    <td className={cn('px-3 py-2 text-right num font-medium', (w.cumulativePnl ?? 0) >= 0 ? 'text-up' : 'text-down')}>
                      {fmtPnLNumber(w.cumulativePnl)}
                    </td>
                    <td className="px-3 py-2 text-right num">{w.signalsTotal}</td>
                    <td className={cn('px-3 py-2 text-right num', w.winRatePct >= 60 ? 'text-up' : w.winRatePct < 40 ? 'text-warn' : 'text-ink-1')}>
                      {fmtPct(w.winRatePct)}
                    </td>
                    <td className="px-3 py-2">
                      {w.cumulativePnl == null ? '暂无数据' : <CumulativeBar value={w.cumulativePnl} max={cumulativeMax} />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-4 w-4 text-warn" />
              Top 5 雇员(30 天胜率)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.topEmployees.map((e, i) => (
              <div key={e.userId} className="flex items-center gap-3 rounded-md bg-bg-3 px-3 py-2">
                <div className={cn('w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold', i === 0 ? 'bg-warn text-white' : i === 1 ? 'bg-ink-3 text-white' : i === 2 ? 'bg-purple text-white' : 'bg-bg-1 text-ink-2')}>
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium num">{e.accountLogin}</div>
                  <div className="text-[11px] text-ink-3 num">{e.totalTrades} 单 · 累计 {fmtPnLNumber(e.cumulativePnl)}</div>
                </div>
                <Pill tone={e.winRatePct >= 60 ? 'up' : e.winRatePct < 40 ? 'warn' : 'brand'}>
                  {fmtPct(e.winRatePct)}
                </Pill>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>品种盈利</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.bySymbol.map((s) => (
              <div key={s.symbol} className="rounded-md bg-bg-3 p-3 space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="font-semibold">{s.symbol}</span>
                  <Pill tone={s.pnl >= 0 ? 'up' : 'down'}>{fmtPnLNumber(s.pnl)}</Pill>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs text-ink-2">
                  <Kv label="信号" value={String(s.totalSignals)} />
                  <Kv label="成交" value={String(s.executed)} />
                  <Kv label="胜率" value={fmtPct(s.winRatePct)} />
                </div>
              </div>
            ))}
            {data.bySymbol.length === 0 && <div className="text-sm text-ink-3">暂无品种数据</div>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function CumulativeBar({ value, max }: { value: number; max: number }) {
  const pct = max > 0 ? Math.min(100, Math.abs(value) / max * 100) : 0;
  const positive = value >= 0;
  return (
    <div className="relative h-3 rounded-full bg-bg-3 overflow-hidden">
      <div
        className={cn('absolute top-0 h-full rounded-full transition-all', positive ? 'bg-up left-1/2' : 'bg-down right-1/2')}
        style={{ width: `${pct / 2}%` }}
      />
      <div className="absolute top-0 left-1/2 h-full w-px bg-ink-3" />
    </div>
  );
}

function Kv({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-ink-3">{label}</div>
      <div className="num text-ink-1">{value}</div>
    </div>
  );
}

function EntryCard({ tone, onClick, icon, title, desc, status }: EntryCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'text-left rounded-lg border p-4 transition-all',
        tone === 'active' ? 'border-brand bg-brand/5 ring-1 ring-brand/30' : 'border-line bg-bg-2 hover:bg-bg-3',
      )}
    >
      <div className="flex items-center gap-2.5 mb-2">
        {icon}
        <div className="text-sm font-semibold text-ink-0">{title}</div>
      </div>
      <div className="text-xs text-ink-2 leading-relaxed">{desc}</div>
      <div className="mt-2 text-[10px] uppercase tracking-wider text-ink-3">{status}</div>
    </button>
  );
}

function SumCard({ label, value, tone = 'neutral', sub }: { label: string; value: string; tone?: 'up' | 'down' | 'warn' | 'neutral'; sub?: React.ReactNode }) {
  const cls = { up: 'text-up', down: 'text-down', warn: 'text-warn', neutral: 'text-ink-0' }[tone];
  return (
    <div className="rounded-lg border border-line bg-bg-2 p-4 elev-card">
      <div className="text-xs uppercase tracking-wide text-ink-2">{label}</div>
      <div className={cn('mt-2 text-2xl font-bold num', cls)}>{value}</div>
      {sub && <div className="mt-1.5">{sub}</div>}
    </div>
  );
}
